import { useEffect, useMemo, useState } from "react";
import { OpportunityCard } from "../components/OpportunityCard";
import { OpportunityHistoryDialog } from "../components/OpportunityHistoryDialog";
import { QuickUpdateDialog } from "../components/QuickUpdateDialog";
import { getOpportunities, updateOpportunity } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { statusLabel } from "../lib/status-labels";
import {
  opportunityStatuses,
  type Opportunity,
  type OpportunityUpdateInput,
} from "../types";

export function OpportunitiesPage() {
  const { session } = useAuth();
  const { locale, t } = useI18n();
  const [items, setItems] = useState<Opportunity[]>([]);
  const [filter, setFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Opportunity | null>(null);
  const [historySelected, setHistorySelected] = useState<Opportunity | null>(
    null,
  );
  useEffect(() => {
    if (session) void getOpportunities(session.accessToken).then(setItems);
  }, [session]);
  const visible = useMemo(() => {
    const query = search.trim().toLocaleLowerCase(locale);

    return items.filter((item) => {
      const matchesStatus = filter === "All" || item.status === filter;
      if (!matchesStatus || !query) return matchesStatus;

      return [
        item.clientReference.displayName,
        item.clientReference.market,
        item.projectName,
        item.projectSummary,
      ].some((value) => value.toLocaleLowerCase(locale).includes(query));
    });
  }, [filter, items, locale, search]);

  async function save(input: OpportunityUpdateInput) {
    if (!session || !selected) return;
    const updated = await updateOpportunity(
      session.accessToken,
      selected.id,
      input,
    );
    setItems((current) =>
      current.map((item) => (item.id === updated.id ? updated : item)),
    );
  }

  return (
    <div className="page">
      <section className="page-heading">
        <div>
          <span className="eyebrow">ESAC</span>
          <h1>{t("opportunities")}</h1>
        </div>
      </section>
      <section className="opportunity-tools" aria-label={t("opportunityTools")}>
        <label className="search-control">
          <span>{t("search")}</span>
          <span className="search-input-wrap">
            <svg aria-hidden="true" viewBox="0 0 24 24">
              <path d="m21 21-4.35-4.35m2.35-5.15a7.5 7.5 0 1 1-15 0 7.5 7.5 0 0 1 15 0Z" />
            </svg>
            <input
              type="search"
              value={search}
              placeholder={t("searchPlaceholder")}
              onChange={(event) => setSearch(event.target.value)}
            />
            {search ? (
              <button
                className="clear-search"
                type="button"
                aria-label={t("clearSearch")}
                onClick={() => setSearch("")}
              >
                ×
              </button>
            ) : null}
          </span>
        </label>
        <label className="filter-control">
          <span>{t("filter")}</span>
          <select
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
          >
            <option value="All">{t("all")}</option>
            {opportunityStatuses.map((status) => (
              <option key={status} value={status}>
                {statusLabel(status, locale)}
              </option>
            ))}
          </select>
        </label>
        <p className="result-count" aria-live="polite">
          {t("showingResults", {
            visible: visible.length,
            total: items.length,
          })}
        </p>
      </section>
      <div className="opportunity-grid">
        {visible.map((item) => (
          <OpportunityCard
            key={item.id}
            opportunity={item}
            onUpdate={() => setSelected(item)}
            onHistory={
              session?.role === "bai_admin"
                ? () => setHistorySelected(item)
                : undefined
            }
          />
        ))}
      </div>
      {!visible.length && <div className="empty-state">{t("noResults")}</div>}
      <QuickUpdateDialog
        opportunity={selected}
        onClose={() => setSelected(null)}
        onSave={save}
      />
      {session?.role === "bai_admin" ? (
        <OpportunityHistoryDialog
          opportunity={historySelected}
          onClose={() => setHistorySelected(null)}
        />
      ) : null}
    </div>
  );
}
